import { computed, defineComponent, h, reactive, ref, toRefs, unref } from 'vue';
import { objectMap, useDraggable, UseDraggableOptions } from '@mts-fe/hooks';
import { $MTS } from '@mts-fe/utils';
import { getGlobalStore } from '@/store';

interface IProps extends UseDraggableOptions<any> {
  modelValue: any[];
  tag?: string;
  target?: string;
  componentData?: unknown;
}
// Need to declare Event thrown here, otherwise it will cause Sortablejs internal dispatch Event, repeated trigger events
const emitEvents = [
  'onUpdate',
  'onStart',
  'onAdd',
  'onRemove',
  'onChoose',
  'onUnchoose',
  'onEnd',
  'onSort',
  'onFilter',
  'onClone',
  'onMove',
  'onChange',
] as const;
const emits = emitEvents.map((key) => key.slice(2).toLowerCase());

const props = [
  'clone',
  'animation',
  'ghostClass',
  'group',
  'sort',
  'disabled',
  'store',
  'handle',
  'draggable',
  'swapThreshold',
  'invertSwap',
  'invertedSwapThreshold',
  'removeCloneOnHide',
  'direction',
  'chosenClass',
  'dragClass',
  'ignore',
  'filter',
  'preventOnFilter',
  'easing',
  'setData',
  'dropBubble',
  'dragoverBubble',
  'dataIdAttr',
  'delay',
  'delayOnTouchOnly',
  'touchStartThreshold',
  'forceFallback',
  'fallbackClass',
  'fallbackOnBody',
  'fallbackTolerance',
  'fallbackOffset',
  'supportPointer',
  'emptyInsertThreshold',
  'scroll',
  'forceAutoScrollFallback',
  'scrollSensitivity',
  'scrollSpeed',
  'bubbleScroll',
  'modelValue',
  'tag',
  'target',
  'componentData',
  'customUpdate',
  ...emitEvents,
] as const;

export const Draggable = defineComponent<IProps>({
  name: 'Draggable',
  model: { prop: 'modelValue', event: 'update:modelValue' },
  props: props as unknown as any,
  emits: ['update:modelValue', ...emits],
  setup(props, { slots, emit, expose, attrs }) {
    const globalStore = getGlobalStore();
    const globalIntensity = globalStore.getDiviceIntensity;
    const globalDelay = globalStore.getDiviceDelay;

    const events: Recordable = emitEvents.reduce((acc, event) => {
      acc[event] = (...args) => {
        const key = event.slice(2).toLowerCase();
        key === 'choose' && $MTS.App.startVib(globalIntensity);
        emit(key, ...args);
      };
      return acc;
    }, {});

    const options = computed(() => {
      const { modelValue, ...rest } = toRefs(props);
      const opt: Recordable = Object.entries(rest).reduce((acc, [key, value]) => {
        // @ts-ignore
        let newValue = unref(value) as any;
        if (key === 'delay' && newValue === void 0) {
          newValue = globalDelay;
        }
        if (newValue !== undefined) acc[key] = newValue;
        return acc;
      }, {});
      return { ...events, ...objectMap({ ...attrs, ...opt }) };
    });

    const list = computed({
      get: () => props.modelValue,
      set: (v) => emit('update:modelValue', v),
    });

    const target = ref();
    const data = reactive(useDraggable((props.target || target) as string, list, options));

    expose(data);

    return () => h(props.tag || 'div', { ref: target }, slots?.default?.(data));
  },
});
